import { useAtomValue, useSetAtom } from "jotai";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useId } from "react";
import { DateRangePresets } from "@/pages/main/reports/components/DateRangePresets";
import { codecTimelineFiltersAtom, licDevicesAtom, selectedPropertyAtom } from "@/store/data";

/**
 * Renders the CODEC timeline controls: the active property, the CODEC picker
 * (the property's LIC devices, with the first auto-selected when none is
 * chosen) and the shared date-range presets. When the property has no LIC the
 * controls are disabled and a hint is shown.
 */
export function CodecTimelineFiltersBar() {
  const codecSelectId = useId();
  const filters = useAtomValue(codecTimelineFiltersAtom);
  const setFilters = useSetAtom(codecTimelineFiltersAtom);
  const property = useAtomValue(selectedPropertyAtom);
  const licDevices = useAtomValue(licDevicesAtom);

  useEffect(() => {
    if (!licDevices.length) {
      if (filters.codecId !== null) {
        setFilters({ codecId: null });
      }
      return;
    }
    if (filters.codecId && licDevices.some((device) => device.id === filters.codecId)) {
      return;
    }
    setFilters({ codecId: licDevices[0].id });
  }, [filters.codecId, licDevices, setFilters]);

  const handleCodecChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters({ codecId: event.target.value || null });
    },
    [setFilters]
  );

  const handlePresetChange = useCallback(
    (range: { start: string; end: string }) => {
      // The filters atom resets pagination and clears entries when the range changes.
      setFilters((prev) => ({ ...prev, startDate: range.start, endDate: range.end }));
    },
    [setFilters]
  );

  const disabled = !property || licDevices.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Propriedade ativa</p>
          <p className="text-sm text-gray-900">{property?.name ?? "Nenhuma propriedade selecionada"}</p>
        </div>
        <div className="w-full md:w-72">
          <label htmlFor={codecSelectId} className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            CODEC
          </label>
          <select
            id={codecSelectId}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={filters.codecId ?? ""}
            onChange={handleCodecChange}
            disabled={disabled}
          >
            {licDevices.length === 0 ? <option value="">Nenhum CODEC disponível</option> : null}
            {licDevices.map((device) => (
              <option key={device.id} value={device.id}>
                {device.identifier}
              </option>
            ))}
          </select>
          {licDevices.length === 0 ? (
            <p className="mt-1 text-xs text-gray-500">Nenhum CODEC encontrado para a propriedade selecionada.</p>
          ) : null}
        </div>
      </div>

      <DateRangePresets
        startDate={filters.startDate}
        endDate={filters.endDate}
        disabled={disabled}
        onApply={handlePresetChange}
      />

      {!filters.codecId ? (
        <p className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 text-xs text-neutral-600">
          Selecione um CODEC para carregar a linha do tempo.
        </p>
      ) : null}
    </div>
  );
}
